import { ApolloClient, InMemoryCache, createHttpLink, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { config } from '../config';
import { authService } from '../services/auth';

// Error handling link
const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.error(`GraphQL error: Message: ${message}, Location: ${locations}, Path: ${path}`);
    });
  }
  if (networkError) {
    console.error(`Network error: ${networkError}`);
  }
});

// HTTP link for GraphQL endpoint
const httpLink = createHttpLink({
  uri: config.graphqlEndpoint,
});

// Authentication link to add Cognito JWT token to requests
const authLink = setContext(async (_, { headers }) => {
  try {
    // Get the current user's JWT token from our auth service
    const session = await authService.getCurrentSession();
    const token = session?.getIdToken().getJwtToken();
    
    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json'
      }
    };
  } catch (error) {
    console.error('Error getting auth token:', error);
    return {
      headers: {
        ...headers,
        'Content-Type': 'application/json'
      }
    };
  }
});

// Create Apollo Client
export const apolloClient = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          listDiseases: {
            keyArgs: false, // Do not cache separate results based on the query arguments
            merge(existing = { items: [] }, incoming, { args }) {
              // If there's no nextToken, it's a new query/refresh, so we take the incoming data.
              if (!args?.nextToken) {
                return incoming;
              }

              // Otherwise, it's a 'fetchMore' call, so we append the new items.
              return {
                ...incoming,
                items: [...(existing.items || []), ...incoming.items],
              };
            },
          },
        },
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      errorPolicy: 'all',
    },
    query: {
      errorPolicy: 'all',
    },
  },
});
